import {Button,Form,Input,Modal,Progress,Radio,Select,Upload,UploadFile,UploadProps,} from "antd";
import React, {useEffect,useMemo,useRef,useState,} from "react";
import { useAppDispatch } from "../../../redux/useAppDispatch";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { InboxOutlined } from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import { v4 as uuidv4 } from "uuid";
import {
    createPack,
    resetUploadProgress,
    uploadFile,
} from "../../../redux/Transcribe/TranscribeAction";
import { fetchRefreshInProgress } from "../../../redux/JobModule/InProgressPage/InProgressAction";
import axios, { CancelTokenSource } from "axios";

type UploadModalProps = {
    close: () => void;
    openState: boolean;
    open: () => void;
};
function UploadModal(props: UploadModalProps) {
    const uploadLoading = useSelector(
        (state: RootState) =>
            state.TranscribeState.createPackLoading
    );

    const tenantData = useSelector(
        (state: RootState) =>
            state.TranscribeState.pickList.tenantPicklist
    );

    const uploadProgress = useSelector(
        (state: RootState) =>
            state.TranscribeState.currentFileUpload
    );

    const cancelUploadRef =
        useRef<CancelTokenSource | null>(null);

    const [confirmClose, setConfirmClose] =
        useState(false);

    const newTenantData = [
        ...tenantData.slice(1, tenantData.length),
    ];

    const [enableUpload, setEnableUpload] =
        useState(false);

    const dispatch = useAppDispatch();

    const [form] = Form.useForm();

    const [fileList, setFileList] =
        useState<UploadFile[]>([]);

    const [uploadUuid, setUploadUuid] =
        useState<string | null>(null);

    const [isFileUploaded, setIsFileUploaded] =
        useState(false);

    const [error, setError] =
        useState<string | null>(null);

    const languagePickList = [
        {
            value: "en-gb",
            label: "English (UK)",
        },
        {
            value: "en-us",
            label: "English (USA)",
        },
    ];

    const isValidFormat = (type: string) => {
        const allowedFormats = ["audio/mpeg","audio/mp3","audio/x-m4a","audio/wav","audio/x-ms-wma","audio/flac","video/mp4","video/x-msvideo","video/mpeg","video/avi","video/quicktime",];
    return allowedFormats.includes(type);
    };

    /*Checks whether ALL mandatory fields have been completed*/
    const checkFormValidity = () => {
        const values = form.getFieldsValue();

        const hasErrors = form
            .getFieldsError()
            .some(
                (field) =>
                    field.errors.length > 0
            );

        const isValid =
            !!values.IdTenant &&
            !!values.JobNumber?.trim() &&
            !!values.ClientName?.trim() &&
            !!values.Language &&
            !!values.TranscriptionType &&
            !!values.PackPriority &&
            !hasErrors;

        setEnableUpload(isValid);

        return isValid;
    };

    const uploadProps: UploadProps = {
        name: "file",
        multiple: false,
        showUploadList: false,

        beforeUpload: (file) => {
            // Reset previous errors and upload state
            setError(null);
            setIsFileUploaded(false);

            // Validate selected file
            const validation = validateFile(file);

            if (!validation.isValid) {
                setError(validation.message);

                // Clear file list
                setFileList([]);

                // Prevent Ant Design from adding the file
                return Upload.LIST_IGNORE;
            }

            // Reset previous file field
            form.resetFields([
                "InputFileName",
            ]);

            // Set selected file
            setFileList([file as any]);

            return false;
        },
    };

    /*
     * Reset the complete form
     */
    const resetForm = () => {
        form.resetFields();

        setFileList([]);

        setUploadUuid(null);

        setIsFileUploaded(false);

        setEnableUpload(false);

        setError(null);

        dispatch(resetUploadProgress());

        cancelUploadRef.current = null;
    };

    /*
     * Upload selected file
     */
    useEffect(() => {
        const uploadFileAsync = async () => {
            if (
                fileList.length > 0 &&
                !error
            ) {
                try {
                    const id = uuidv4();

                    setUploadUuid(id);

                    await dispatch(
                        uploadFile({
                            data: {
                                IdTenant:
                                    form.getFieldValue(
                                        "IdTenant"
                                    ),
                            },
                            file: fileList[0],
                            id: id,
                            cancelUploadRef:
                                cancelUploadRef,
                        })
                    ).unwrap();

                    setIsFileUploaded(true);
                } catch (error) {
                    setIsFileUploaded(false);

                    setError(
                        "An error occurred while uploading the file."
                    );
                }
            }
        };

        uploadFileAsync();
    }, [fileList, error]);

    /*
     * Reset modal state when modal is closed
     */
    useEffect(() => {
        return () => {
            dispatch(resetUploadProgress());

            form.resetFields();

            setFileList([]);

            setEnableUpload(false);

            setIsFileUploaded(false);

            setError(null);

            setUploadUuid(null);

            cancelUploadRef.current = null;
        };
    }, [props.openState]);

    const UploadModalContent = useMemo(() => {
        const onFinish = async (value: any) => {
            const id = uploadUuid;

            /* Convert spaces to underscores for backend storage */
            const jobNumber = value.JobNumber
                ?.trim()
                .replace(/\s+/g, "_");

            const payload = {
                Uuid: id,

                IdTenant: value.IdTenant,

                JobNumber: jobNumber,

                ClientName:
                    value.ClientName?.trim(),

                TranscriptionType:
                    value.TranscriptionType,

                FileName:
                    fileList[0].name,

                Language:
                    value.Language,

                PackPriority:
                    value.PackPriority,
            };

            console.log(
                "Transcribe Payload Data:",
                payload
            );

            try {
                await dispatch(
                    createPack({
                        data: payload,
                        file: fileList[0],
                        id: id,
                    })
                ).unwrap();

                await dispatch(fetchRefreshInProgress({})).unwrap();

                props.close();

                resetForm();
            } catch (error: any) {
                const errorMsg = error?.message || error?.response?.data?.message || "An error occurred while creating the pack.";
                setError(errorMsg);
            }
        };

        return (
            <div className="d-flex flex-column justify-content-between gap-4 h-100 w-100">

                <h4>Upload Audio</h4>

                <div className="d-flex flex-column gap-2">

                    <Form
                        form={form}
                        name="createAudioFrom"
                        className="d-flex flex-column"

                        /*Check form every time a field changes.*/
                        onChange={() => {
                            setTimeout(() => {
                                checkFormValidity();
                            }, 0);
                        }}

                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        onFinish={onFinish}
                        initialValues={{
                            isDummyDomain: false,
                        }}
                        colon={false}
                    >

                        <div className="d-flex flex-row flex-1 gap-2">

                            <div className="d-flex flex-column gap-3 w-100">

                                {/* Job number */}
                                <Form.Item
                                    className="form-item"
                                    label="Job Number"
                                    name="JobNumber"
                                    labelAlign="left"

                                    /* Only:A-Z,a-z,0-9,spaces,No special characters,No leading spaces,Multiple spaces become a single space*/
                                     normalize={(value) => {
                                        return value
                                            .replace(
                                                /[^a-zA-Z0-9 ]/g,
                                                ""
                                            )
                                            .replace(
                                                /^\s+/,
                                                ""
                                            )
                                            .replace(
                                                /\s+/g,
                                                " "
                                            )
                                            .slice(0, 40);
                                    }}

                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message:
                                                "Please provide a valid Job Number.",
                                        },
                                        {
                                            max: 40,
                                            message:
                                                "Job Number cannot exceed 40 characters.",
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter Job Number"

                                        onChange={(e) => {
                                            const val =
                                                e.target.value;

                                            /*
                                              Show warning when user enters a special char
                                            */
                                            if (
                                                val &&
                                                /[^a-zA-Z0-9 ]/.test(
                                                    val.slice(-1)
                                                )
                                            ) {
                                                setError(
                                                    "Special characters are restricted. Use only Alphanumeric characters."
                                                );

                                                setTimeout(() => {
                                                    setError(null);
                                                }, 5000);
                                            }

                                            if (val.length > 40) {
                                                setError(
                                                    "Job Number cannot exceed 40 characters."
                                                );

                                                setTimeout(() => {
                                                    setError(null);
                                                }, 5000);
                                            }

                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}

                                        onBlur={() => {
                                            const currentValue =
                                                form.getFieldValue(
                                                    "JobNumber"
                                                );

                                            if (
                                                currentValue
                                            ) {
                                                form.setFieldsValue(
                                                    {
                                                        JobNumber:
                                                            currentValue.trim(),
                                                    }
                                                );
                                            }

                                            checkFormValidity();
                                        }}
                                    />
                                </Form.Item>

                                {/*
                                    REQUESTOR
                                 */}
                                <Form.Item
                                    className="form-item"
                                    label="Requestor"
                                    name="ClientName"
                                    labelAlign="left"


                                    normalize={(value) => {
                                        return value
                                            .replace(
                                                /[^a-zA-Z0-9 ]/g,
                                                ""
                                            )
                                            .replace(
                                                /^\s+/,
                                                ""
                                            )
                                            .replace(
                                                /\s+/g,
                                                " "
                                            )
                                            .slice(0, 40);
                                    }}

                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message:
                                                "Please provide a valid Requestor.",
                                        },
                                        {
                                            max: 40,
                                            message:
                                                "Requestor cannot exceed 40 characters.",
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter Requestor"

                                        onChange={(e) => {
                                            const val =
                                                e.target.value;

                                            if (
                                                val &&
                                                /[^a-zA-Z0-9 ]/.test(
                                                    val.slice(-1)
                                                )
                                            ) {
                                                setError(
                                                    "Special characters are restricted. Use only Alphanumeric characters."
                                                );

                                                setTimeout(() => {
                                                    setError(null);
                                                }, 5000);
                                            }

                                            if (val.length > 40) {
                                                setError(
                                                    "Requestor cannot exceed 40 characters."
                                                );

                                                setTimeout(() => {
                                                    setError(null);
                                                }, 5000);
                                            }

                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}


                                        onBlur={() => {
                                            const currentValue =
                                                form.getFieldValue(
                                                    "ClientName"
                                                );

                                            if (
                                                currentValue
                                            ) {
                                                form.setFieldsValue(
                                                    {
                                                        ClientName:
                                                            currentValue.trim(),
                                                    }
                                                );
                                            }

                                            checkFormValidity();
                                        }}
                                    />
                                </Form.Item>

                                {}
                                <Form.Item
                                    className="form-item"
                                    label="Organization"
                                    name="IdTenant"
                                    labelAlign="left"
                                    required={true}
                                    rules={[
                                        {
                                            required: true,
                                            pattern:
                                                /^[0-9]*$/,
                                            message:
                                                "Please provide a valid Organization.",
                                        },
                                    ]}
                                    validateTrigger="onBlur"
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Select an Organization"
                                        options={
                                            newTenantData
                                        }
                                        onChange={() => {
                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}
                                    />
                                </Form.Item>

                                {}
                                <Form.Item
                                    className="form-item"
                                    label="Language"
                                    name="Language"
                                    labelAlign="left"
                                    required={true}
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select a Language.",
                                        },
                                    ]}
                                    validateTrigger="onBlur"
                                >
                                    <Select
                                        showSearch
                                        options={
                                            languagePickList
                                        }
                                        placeholder="Select a Language"
                                        optionFilterProp="children"
                                        onChange={() => {
                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}
                                    />
                                </Form.Item>

                                {}
                                <Form.Item
                                    className="form-item"
                                    label="Audio Type"
                                    name="TranscriptionType"
                                    labelAlign="left"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select an Audio Type.",
                                        },
                                    ]}
                                >
                                    <Radio.Group
                                        onChange={() => {
                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}
                                    >
                                        <Radio value="Dictation">
                                            Dictation
                                        </Radio>

                                        <Radio value="Transcription">
                                            Pure Transcription
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>

                                {}
                                <Form.Item
                                    className="form-item"
                                    label="Priority"
                                    name="PackPriority"
                                    labelAlign="left"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please select a Priority.",
                                        },
                                    ]}
                                >
                                    <Radio.Group
                                        onChange={() => {
                                            setTimeout(() => {
                                                checkFormValidity();
                                            }, 0);
                                        }}
                                    >
                                        <Radio value="Standard">
                                            Standard
                                        </Radio>

                                        <Radio value="Express">
                                            Express
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>

                            {}
                            <div className="w-100 d-flex flex-column">

                                <Form.Item
                                    className="w-100 h-100"
                                    labelCol={{ span: 0 }}
                                    wrapperCol={{
                                        span: 24,
                                    }}

                                    getValueFromEvent={(
                                        e
                                    ) => {
                                        return e
                                            .fileList[0];
                                    }}

                                    name="InputFileName"
                                    labelAlign="left"
                                    required={true}

                                    rules={[
                                        {
                                            validator: (
                                                _,
                                                value
                                            ) => {
                                                if (
                                                    !value ||
                                                    !value.type
                                                ) {
                                                    return Promise.reject(
                                                        "Please upload a file."
                                                    );
                                                }

                                                if (
                                                    !isValidFormat(
                                                        value.type
                                                    )
                                                ) {
                                                    return Promise.reject(
                                                        "Invalid file format. Please upload a valid audio or video file (.mp3, .m4a, .mp4, .wav, .wma, .avi, .mpeg, .mov, .flac)."
                                                    );
                                                }

                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >

                                    <Dragger
                                        /*
                                         Dragger will be enable donly after all manditory fields have values
                                         */
                                        disabled={
                                            !enableUpload
                                        }

                                        height={385}

                                        accept=".mp3, .m4a, .mp4, .wav, .wma, .avi, .mpeg, .mov, .flac"

                                        {...uploadProps}
                                    >

                                        {fileList.length > 0 ? (

                                            <div className="d-flex flex-column align-items-center">

                                                {
                                                    fileList[0]
                                                        .name
                                                }

                                            </div>

                                        ) : (

                                            <div>

                                                <p className="ant-upload-drag-icon">
                                                    <InboxOutlined />
                                                </p>

                                                <p className="ant-upload-text">
                                                    Click or drag an
                                                    audio file to
                                                    this area to
                                                    upload
                                                </p>

                                                <p className="ant-upload-hint">
                                                    Allowed formats:
                                                    <br />

                                                    <strong>
                                                        .mp3,
                                                        .m4a,
                                                        .mp4,
                                                        .wav,
                                                        .wma,
                                                        .avi,
                                                        .mpeg,
                                                        .mov,
                                                        .flac
                                                    </strong>
                                                </p>

                                            </div>
                                        )}

                                    </Dragger>

                                    {/* Upload Progress */}
                                    {cancelUploadRef.current && (
                                        <Progress
                                            percent={
                                                uploadProgress.uploadProgress
                                            }
                                            strokeColor="#52c41a"
                                            showInfo={true}
                                            style={{
                                                margin: "10px",
                                            }}
                                        />
                                    )}

                                </Form.Item>
                            </div>
                        </div>

                        {/* 
                            ERROR MESSAGE
                         */}
                        {error && (
                            <div className="error justify-content-end align-self-end mb-2">
                                {error}
                            </div>
                        )}

                        {/*
                            TRANSCRIBE BUTTON
                        */}
                        <div className="d-flex gap-3 justify-content-end">

                            <Button
                                className={`button radius ${!isFileUploaded
                                        ? "custom-disabled-look"
                                        : ""
                                    }`}
                                type="primary"

                                disabled={
                                    !isFileUploaded ||
                                    !enableUpload
                                }

                                loading={
                                    uploadLoading
                                }

                                htmlType="submit"

                                onClick={(e) => {
                                    if (
                                        !isFileUploaded
                                    ) {
                                        e.preventDefault();
                                        return;
                                    }
                                }}
                            >
                                Transcribe
                            </Button>

                        </div>

                    </Form>
                </div>
            </div>
        );
    }, [
        form,
        props,
        fileList,
        uploadProgress,
        enableUpload,
        uploadUuid,
        uploadLoading,
        isFileUploaded,
        error,
    ]);

    return (
        <Modal
            centered
            zIndex={1500}
            width={1000}
            footer={[]}

            onCancel={() => {
                if (
                    cancelUploadRef.current
                ) {
                    const shouldClose =
                        window.confirm(
                            "Are you sure you to cancel the upload?"
                        );

                    if (shouldClose) {
                        cancelUploadRef.current?.cancel(
                            "Upload cancelled"
                        );

                        resetForm();

                        setConfirmClose(
                            false
                        );

                        props.close();
                    }

                    return;
                } else {
                    resetForm();

                    props.close();
                }
            }}

            open={props.openState}
        >
            {UploadModalContent}
        </Modal>
    );
}

export default UploadModal;


/*

 FILE VALIDATION

*/

const validateFile = (file: File) => {
    const allowedFormats = [
        "audio/mpeg",
        "audio/mp3",
        "audio/x-m4a",
        "audio/wav",
        "audio/x-ms-wma",
        "audio/flac",
        "video/mp4",
        "video/x-msvideo",
        "video/mpeg",
        "video/avi",
        "video/quicktime",
    ];

    /*
     * Filename allowed characters:
     *
     * A-Z
     * a-z
     * 0-9
     * .
     * _
     * -
     */
    const nameRegex =
        /^[a-zA-Z0-9._-]+$/;

    /*
     * Check file MIME type
     */
    if (!allowedFormats.includes(file.type)) {
        return {
            isValid: false,

            message:
                "Invalid file format.",
        };
    }

    /*
     * Check filename
     */
    if (!nameRegex.test(file.name)) {
        return {
            isValid: false,

            message:
                "Warning: Filename contains special characters (&, *, %,^,$...). Please rename the before uploading.",
        };
    }

    return {
        isValid: true,

        message: "",
    };
};